import { reactive, Ref, ref } from 'vue';
import { defineStore } from 'pinia';
import { AggregateCount, Filter, SearchQuery } from './types';
import { AgeFilter } from './content-filters';
import { FilterOption, Field, SearchFilter, SingleField } from './search-panel-types';
import { v4 as uuidv4 } from 'uuid';

export const useSearchFilterStore = defineStore('search-filter', () => {
  const selectedFilters = reactive<{ value: SearchFilter }>({ value: {} });
  const fields: Ref<Field[]> = ref([]);
  const alias = ref(''); // This is a unique identifier for the filter instance

  const setAlias = (newAlias?: string) => {
    alias.value = newAlias || uuidv4();
  };

  const filterVisibleFields = (fields: Field[]): Field[] => {
    return fields
      .filter((field) => !field.isHidden)
      .map((field) => ('fields' in field ? { ...field, fields: filterVisibleFields(field.fields) } : field));
  };

  const setFields = (filterFields: Field[], filtersAlias?: string) => {
    if (alias.value === '') {
      setAlias();
    }
    // If the alias is the same, we don't need to update the fields
    // example we back from material details to material list, we don't want to reset the filters
    if (fields.value.length > 0 && alias.value === filtersAlias) {
      return;
    }
    setAlias(filtersAlias);
    selectedFilters.value = {};
    fields.value = filterVisibleFields(filterFields);
  };

  const checkIsFilterEmpty = (filterKey: string) => {
    if (selectedFilters.value[filterKey]?.length === 0) {
      delete selectedFilters.value[filterKey];
    }
  };

  const getSingleFieldByKey = (key: string): SingleField | undefined => {
    const singleFields: SingleField[] = fields.value.reduce((result, field) => {
      if ('fields' in field) {
        return [...result, ...field.fields];
      }
      return [...result, field];
    }, [] as SingleField[]);

    return singleFields.find((i) => i.aggregationKey === key);
  };

  const updateSelectedFilters = (filter: SearchFilter) => {
    const filterKey = Object.keys(filter)[0];
    selectedFilters.value = { ...selectedFilters.value, ...filter };
    checkIsFilterEmpty(filterKey);
  };
  const removeAllFilters = () => {
    selectedFilters.value = {};
  };

  const getAgeFilterItems = (minAge: number, maxAge: number): AggregateCount[] => [
    { key: minAge.toString(), count: 1 },
    { key: maxAge.toString(), count: 1 },
  ];

  const removeSelectedFilterByValue = (filterKey: string, option: FilterOption) => {
    if (
      selectedFilters.value[filterKey].length === 2 &&
      fields.value.find((field) => field.aggregationKey === filterKey)?.type === 'multiple_predefined_ranges'
    ) {
      selectedFilters.value[filterKey] = [];
    } else {
      selectedFilters.value[filterKey] = selectedFilters.value[filterKey].filter((item) => item.value !== option.value);
    }
    checkIsFilterEmpty(filterKey);
  };

  const getFormattedFilters = (minAge?: number, maxAge?: number): Filter => {
    let prepared: Filter = {};
    Object.keys(selectedFilters.value).forEach((key: string) => {
      const field: SingleField | undefined = getSingleFieldByKey(key);
      if (field && field.aggregationKey === 'ages') {
        prepared = {
          ...prepared,
          ...AgeFilter(Number(selectedFilters.value[key][0].value), Number(selectedFilters.value[key][1].value)),
        };
        return;
      }
      if (field && (field.type === 'range' || field.type === 'multiple_predefined_ranges')) {
        prepared[`${key}`] = `[${selectedFilters.value[key][0].value} TO ${selectedFilters.value[key][1].value}]`;
        return;
      }
      prepared[key] = selectedFilters.value[key].reduce(
        (result: string[], item: FilterOption) => [...result, item.value],
        [],
      );
    });
    if (!prepared.ages && minAge && maxAge) {
      const ages = getAgeFilterItems(minAge, maxAge);
      return { ...prepared, ...AgeFilter(Number(ages[0].key), Number(ages[1].key)) };
    }
    return prepared;
  };

  const getAggregationKeys = (items: Field[]): string[] =>
    items.reduce((arrangedFields: string[], item: Field) => {
      if ('fields' in item) {
        return [...arrangedFields, ...getAggregationKeys(item.fields)];
      }
      if (!item.aggregate) {
        return arrangedFields;
      }
      return [item.aggregationKey, ...arrangedFields];
    }, []);

  const getFieldsToArrange = (): string[] => getAggregationKeys(fields.value);

  const applyFiltersFromQuery = (query: SearchQuery) => {
    const filters: SearchFilter = {};
    if (!query.filter || Object.keys(query.filter).length === 0) {
      return;
    }
    Object.keys(query.filter).forEach((queryKey) => {
      if (!query.filter || !query.filter[queryKey]) {
        return;
      }
      const queryFilter = query.filter[queryKey];
      if (Array.isArray(queryFilter)) {
        filters[queryKey] = queryFilter.map((item) => ({ value: item, label: item, count: 1 }));
      } else if (typeof queryFilter !== 'string') {
        throw new Error(`Invalid filter type ${typeof queryFilter} for ${queryKey}`);
      } else {
        filters[queryKey] = [{ value: queryFilter, label: queryFilter, count: 1 }];
      }
    });
    updateSelectedFilters(filters);
  };

  return {
    selectedFilters,
    fields,
    getAgeFilterItems,
    updateSelectedFilters,
    removeAllFilters,
    removeSelectedFilterByValue,
    getFormattedFilters,
    getFieldsToArrange,
    applyFiltersFromQuery,
    setFields,
  };
});
